import React, { useEffect, useRef, useState } from "react";
import {
  Typography, Box, Button, Tooltip, Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarIcon from "@mui/icons-material/Star";
import PricingDialog from "./PricingDialog";

const PricingCard = ({ plan, user, planType, isPopular, handleShowYearly, handleShowMonthly }) => {
  const [open, setOpen] = useState(false);
  const descriptionElementRef = useRef(null);

  useEffect(() => {
    if (open && descriptionElementRef.current) {
      descriptionElementRef.current.focus();
    }
  }, [open]);

  const price = plan.price_cents / 100;
  const interval = plan.name === "Diamond Special" ? "5 years" : plan.interval;

  return (
    <>
      <Box
        sx={{
          position: "relative",
          backgroundColor: "#1a1a1a",
          border: isPopular ? "2px solid #FFC001" : "1px solid #2a2a2a",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.25s ease, box-shadow 0.25s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: isPopular
              ? "0 20px 50px rgba(255,192,1,0.18)"
              : "0 12px 32px rgba(0,0,0,0.4)",
          },
        }}
      >
        {/* Popular badge */}
        {isPopular && (
          <Box sx={{
            position: "absolute", top: 0, right: 0,
            backgroundColor: "#FFC001", color: "#000",
            px: 2, py: 0.6, borderBottomLeftRadius: "12px",
            display: "flex", alignItems: "center", gap: 0.5,
          }}>
            <StarIcon sx={{ fontSize: 13 }} />
            <Typography sx={{ fontWeight: 800, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Most Popular
            </Typography>
          </Box>
        )}

        {/* Header */}
        <Box sx={{
          p: 3, pb: 2.5,
          borderBottom: "1px solid #222",
          background: isPopular
            ? "linear-gradient(135deg, rgba(255,192,1,0.08) 0%, transparent 60%)"
            : "transparent",
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: "18px", color: "#fff", mb: 0.5 }}>
            {plan.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 1.5 }}>
            <Typography sx={{ fontSize: "13px", color: "#888", fontWeight: 600, alignSelf: "flex-start", mt: 0.5 }}>$</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: "44px", color: isPopular ? "#FFC001" : "#fff", lineHeight: 1 }}>
              {price % 1 === 0 ? price : price.toFixed(2)}
            </Typography>
            <Typography sx={{ color: "#555", fontSize: "13px", mb: 0.5, alignSelf: "flex-end" }}>
              / {interval}
            </Typography>
          </Box>
        </Box>

        {/* Features */}
        <Box sx={{ p: 3, flex: 1 }}>
          {plan.offers && plan.offers.slice(0, 8).map((offer) => (
            <Box key={offer.id} sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
              {offer.value
                ? <CheckCircleIcon sx={{ color: "#FFC001", fontSize: 17, flexShrink: 0 }} />
                : <CancelIcon sx={{ color: "#333", fontSize: 17, flexShrink: 0 }} />
              }
              <Typography sx={{ color: offer.value ? "#ccc" : "#555", fontSize: "14px", flex: 1 }}>
                {offer.name}
              </Typography>
              {offer.description && (
                <Tooltip title={offer.description} arrow enterTouchDelay={0}>
                  <InfoOutlinedIcon sx={{ color: "#444", fontSize: 15, cursor: "pointer", flexShrink: 0, "&:hover": { color: "#FFC001" } }} />
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>

        {/* CTA */}
        <Box sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setOpen(true)}
            fullWidth
            variant={isPopular ? "contained" : "outlined"}
            sx={{
              height: 46, fontWeight: 800, fontSize: "14px", textTransform: "none", borderRadius: "12px",
              ...(isPopular
                ? { backgroundColor: "#FFC001", color: "#000", "&:hover": { backgroundColor: "#e6ac00" } }
                : { borderColor: "#333", color: "#ccc", "&:hover": { borderColor: "#FFC001", color: "#FFC001", backgroundColor: "rgba(255,192,1,0.05)" } }
              ),
            }}
          >
            Get Started
          </Button>
        </Box>
      </Box>

      <PricingDialog
        plan={plan}
        open={open}
        user={user}
        handleClose={() => setOpen(false)}
        descriptionElementRef={descriptionElementRef}
      />
    </>
  );
};

export default PricingCard;
